import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, User, Trophy, Star, Heart, Users, UserSearch, Clock } from "../components/common/Icons";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// UserPlus icon
const UserPlus = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <line x1="19" y1="8" x2="19" y2="14"/>
    <line x1="22" y1="11" x2="16" y2="11"/>
  </svg>
);

// User Search Component
export const UserSearchBar = ({ onUserSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const searchUsers = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }
      
      setLoading(true);
      try {
        const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`);
        setResults(res.data.users || []);
      } catch (e) {
        console.error('Error searching users:', e);
      }
      setLoading(false);
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  return (
    <div className="relative" data-testid="user-search-bar">
      <div className="relative">
        <UserSearch size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Buscar jugadores..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
          onFocus={() => setShowResults(true)}
          className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-purple-700 rounded-lg text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-pink-500"
          data-testid="user-search-input"
        />
      </div>
      
      {showResults && (query.length >= 2 || results.length > 0) && (
        <div className="absolute z-50 w-full mt-2 bg-gray-900 border border-purple-700 rounded-lg shadow-xl max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-400">
              <div className="inline-block w-5 h-5 border-2 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              {query.length < 2 ? 'Escribe al menos 2 caracteres' : 'No se encontraron usuarios'}
            </div>
          ) : (
            <div className="divide-y divide-purple-800/50">
              {results.map(user => (
                <button
                  key={user.user_id}
                  onClick={() => { onUserSelect(user); setShowResults(false); setQuery(''); }}
                  className="w-full p-3 flex items-center gap-3 hover:bg-purple-900/30 transition-colors text-left"
                  data-testid={`user-result-${user.user_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white font-bold text-sm">{user.name?.charAt(0) || '?'}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Public User Profile Page
const PublicProfilePage = ({ currentUser, isLoggedIn }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [friendshipStatus, setFriendshipStatus] = useState('none'); // none, pending, accepted, received
  const [myReaction, setMyReaction] = useState(null);
  const [canViewContent, setCanViewContent] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/profile/${userId}`);
      setProfile(res.data);
      
      // Check friendship status if logged in
      if (isLoggedIn && currentUser?.user_id !== userId) {
        try {
          const friendRes = await axios.get(`${API}/users/friendship/${userId}`, { withCredentials: true });
          setFriendshipStatus(friendRes.data.status || 'none');
          setCanViewContent(friendRes.data.status === 'accepted');
        } catch (e) {
          setFriendshipStatus('none');
          setCanViewContent(false);
        }
      } else if (currentUser?.user_id === userId) {
        setCanViewContent(true); // Own profile
      }
    } catch (e) {
      console.error('Error loading profile:', e);
    }
    setLoading(false);
  }, [userId, isLoggedIn, currentUser?.user_id]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleFriendRequest = async () => {
    if (!isLoggedIn) return;
    try {
      const res = await axios.post(`${API}/users/friend-request/${userId}`, {}, { withCredentials: true });
      setFriendshipStatus(res.data.status);
      if (res.data.status === 'accepted') {
        setCanViewContent(true);
      }
    } catch (e) {
      console.error('Error with friend request:', e);
      alert(e.response?.data?.detail || 'Error al enviar solicitud');
    }
  };

  const handleAcceptRequest = async () => {
    if (!isLoggedIn) return;
    try {
      await axios.post(`${API}/users/friend-accept/${userId}`, {}, { withCredentials: true });
      setFriendshipStatus('accepted');
      setCanViewContent(true);
    } catch (e) {
      console.error('Error accepting request:', e);
    }
  };

  const handleReject = async () => {
    if (!isLoggedIn) return;
    try {
      await axios.post(`${API}/users/friend-reject/${userId}`, {}, { withCredentials: true });
      setFriendshipStatus('none');
    } catch (e) {
      console.error('Error rejecting request:', e);
    }
  };

  const handleReact = async (reactionType) => {
    if (!isLoggedIn) return;
    try {
      await axios.post(`${API}/users/react/${userId}`, { reaction: reactionType }, { withCredentials: true });
      setMyReaction(reactionType);
      loadProfile();
    } catch (e) {
      console.error('Error reacting:', e);
    }
  };

  // Render friend button based on status
  const renderFriendButton = () => {
    switch (friendshipStatus) {
      case 'pending':
        return (
          <button
            disabled
            className="px-6 py-2.5 rounded-lg font-semibold text-sm bg-yellow-900/50 text-yellow-300 border border-yellow-600/50 cursor-not-allowed"
            data-testid="friend-pending-btn"
          >
            <Clock size={16} className="inline mr-2" />
            Solicitud Enviada
          </button>
        );
      case 'received':
        return (
          <div className="flex flex-col gap-2">
            <span className="text-xs text-yellow-400 text-center">Te envió solicitud</span>
            <div className="flex gap-2">
              <button
                onClick={handleAcceptRequest}
                className="px-4 py-2 rounded-lg font-semibold text-sm bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:shadow-lg transition-all"
                data-testid="accept-friend-btn"
              >
                Aceptar
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 rounded-lg font-semibold text-sm bg-red-900/50 text-red-300 border border-red-600/50 hover:bg-red-800/50"
                data-testid="reject-friend-btn"
              >
                Rechazar
              </button>
            </div>
          </div>
        );
      case 'accepted':
        return (
          <button
            disabled
            className="px-6 py-2.5 rounded-lg font-semibold text-sm bg-green-900/50 text-green-300 border border-green-600/50"
            data-testid="friends-btn"
          >
            <Users size={16} className="inline mr-2" />
            Amigos
          </button>
        );
      default:
        return (
          <button
            onClick={handleFriendRequest}
            className="px-6 py-2.5 rounded-lg font-semibold text-sm bg-gradient-to-r from-pink-500 to-red-500 text-white hover:shadow-lg hover:shadow-pink-500/30 transition-all"
            data-testid="add-friend-btn"
          >
            <UserPlus size={16} className="inline mr-2" />
            Enviar Solicitud
          </button>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-20 flex items-center justify-center">
        <div className="inline-block w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-black pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Usuario no encontrado</h1>
          <Link to="/" className="text-pink-400 hover:text-pink-300">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser?.user_id === userId;

  return (
    <div className="min-h-screen bg-black pt-20" data-testid="public-profile-page">
      <div className="container mx-auto px-4 py-4">
        <Link to="/ranking" className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 transition-colors text-sm">
          <ArrowLeft /> Volver al Ranking
        </Link>
      </div>

      <div className="container mx-auto px-4 py-6 md:py-8 max-w-4xl">
        {/* Profile Header */}
        <div className="glass-card rounded-xl md:rounded-2xl p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
            <div className="relative">
              <div className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center overflow-hidden ring-4 ring-purple-500/30">
                {profile.picture ? (
                  <img src={profile.picture} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-black text-4xl">{profile.name?.charAt(0) || '?'}</span>
                )}
              </div>
              {profile.ranking_position && profile.ranking_position <= 3 && (
                <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-xl shadow-lg">
                  {profile.ranking_position === 1 ? '🥇' : profile.ranking_position === 2 ? '🥈' : '🥉'}
                </div>
              )}
            </div>
            
            <div className="text-center md:text-left flex-1">
              <h1 className="text-2xl md:text-3xl font-black text-white mb-1">{profile.name}</h1>
              <p className="text-gray-400 text-sm mb-4">Miembro desde {new Date(profile.created_at).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}</p>
              
              <div className="flex flex-wrap justify-center md:justify-start gap-3">
                {profile.ranking_position && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-900/50 border border-purple-600/50 rounded-full">
                    <Trophy size={16} className="text-yellow-400" />
                    <span className="font-bold text-purple-300 text-sm">Ranking #{profile.ranking_position}</span>
                  </div>
                )}
                {profile.ranking_total && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-900/50 border border-amber-600/50 rounded-full">
                    <Star filled className="text-amber-400" />
                    <span className="font-bold text-amber-300 text-sm">{profile.ranking_total} puntos</span>
                  </div>
                )}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-pink-900/50 border border-pink-600/50 rounded-full">
                  <Heart className="text-pink-400" />
                  <span className="font-bold text-pink-300 text-sm">{profile.reactions_received || 0} reacciones</span>
                </div>
              </div>
            </div>
            
            {/* Actions */}
            {!isOwnProfile && isLoggedIn && (
              <div className="flex flex-col gap-2">
                {renderFriendButton()}
              </div>
            )}
          </div>
        </div>

        {/* Private Content Notice */}
        {!canViewContent && !isOwnProfile && (
          <div className="glass-card rounded-xl p-6 mb-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-purple-900/50 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-400">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Contenido Privado</h3>
            <p className="text-gray-400 text-sm mb-4">
              {friendshipStatus === 'pending' 
                ? 'Tu solicitud de amistad está pendiente. Cuando sea aceptada podrás ver el contenido completo.'
                : 'Envía una solicitud de amistad para ver el contenido completo de este perfil.'}
            </p>
          </div>
        )}

        {/* Reactions Section */}
        {!isOwnProfile && isLoggedIn && canViewContent && (
          <div className="glass-card rounded-xl p-4 mb-6">
            <h3 className="text-sm text-gray-400 mb-3">Reacciona al perfil</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { type: 'like', icon: '👍', label: 'Me gusta' },
                { type: 'celebrate', icon: '🎉', label: 'Celebrar' },
                { type: 'fire', icon: '🔥', label: 'Fuego' },
                { type: 'trophy', icon: '🏆', label: 'Trofeo' },
              ].map(reaction => (
                <button
                  key={reaction.type}
                  onClick={() => handleReact(reaction.type)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                    myReaction === reaction.type
                      ? 'bg-pink-500/30 border border-pink-500 text-white'
                      : 'bg-purple-900/30 border border-purple-700 text-gray-300 hover:bg-purple-800/50'
                  }`}
                  data-testid={`react-${reaction.type}`}
                >
                  <span>{reaction.icon}</span>
                  <span>{reaction.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Stats Grid - Only visible if friends or own profile */}
        {canViewContent && (
          <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
            <div className="glass-card rounded-lg p-4 text-center">
              <Trophy size={24} className="mx-auto text-yellow-400 mb-2" />
              <p className="text-xl md:text-2xl font-black text-yellow-400">{profile.ranking_position || '-'}</p>
              <p className="text-gray-400 text-xs">Posición</p>
            </div>
            <div className="glass-card rounded-lg p-4 text-center">
              <Star filled className="mx-auto text-pink-400 mb-2" />
              <p className="text-xl md:text-2xl font-black text-pink-400">{profile.ranking_total || 0}</p>
              <p className="text-gray-400 text-xs">Puntos</p>
            </div>
            <div className="glass-card rounded-lg p-4 text-center">
              <Heart className="mx-auto text-red-400 mb-2" />
              <p className="text-xl md:text-2xl font-black text-red-400">{profile.reactions_received || 0}</p>
              <p className="text-gray-400 text-xs">Reacciones</p>
            </div>
          </div>
        )}

        {/* Featured Cards - Only visible if friends or own profile */}
        {canViewContent && profile.featured_cards && profile.featured_cards.length > 0 && (
          <div className="glass-card rounded-xl p-4 md:p-6">
            <h2 className="text-lg font-bold text-yellow-400 mb-4">Cartas Destacadas</h2>
            <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
              {profile.featured_cards.map((card, index) => (
                <div key={index} className="aspect-[3/4] bg-purple-900/50 rounded-lg overflow-hidden">
                  <img src={card.image_url} alt={card.name} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicProfilePage;
